import {
  Box, Card, CardContent, Chip, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import EventIcon from '@mui/icons-material/Event';

import { PageHeader } from '@/components/PageHeader';
import { DataState } from '@/components/DataState';
import { useApiQuery } from '@/hooks/useApiQuery';
import { practice } from '@/api/endpoints';
import { formatDateTime } from '@/utils/format';

type AppointmentRow = {
  id: string;
  status: string;
  modality: string;
  start_at: string;
  clinician_name: string;
  patient_initials: string;
  procedure_name: string;
  payment_status: string;
};

const STATUS_FILTERS = [
  { code: 'all',          label: 'All' },
  { code: 'upcoming',     label: 'Upcoming' },
  { code: 'completed',    label: 'Completed' },
  { code: 'cancelled',    label: 'Cancelled' },
];

export function PracticeAppointmentsPage() {
  const q = useApiQuery({
    queryKey: ['practice', 'appointments'],
    queryFn:  () => practice.appointments.list() as Promise<AppointmentRow[]>,
  });
  const [filter, setFilter] = useState('all');

  const filtered = useMemo(() => {
    const all = q.data ?? [];
    const now = new Date();
    if (filter === 'upcoming') {
      return all.filter((a) => new Date(a.start_at) >= now &&
        ['booked', 'confirmed', 'checked_in', 'in_consult'].includes(a.status));
    }
    if (filter === 'completed') {
      return all.filter((a) => a.status === 'completed');
    }
    if (filter === 'cancelled') {
      return all.filter((a) =>
        ['cancelled_pt', 'cancelled_dr', 'no_show', 'refunded'].includes(a.status));
    }
    return all;
  }, [q.data, filter]);

  return (
    <>
      <PageHeader title="Appointments" subtitle="All bookings across clinicians at this practice." />

      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <TextField select size="small" label="Filter" sx={{ minWidth: 180 }}
                   value={filter} onChange={(e) => setFilter(e.target.value)}>
          {STATUS_FILTERS.map((f) => (
            <MenuItem key={f.code} value={f.code}>{f.label}</MenuItem>
          ))}
        </TextField>
      </Stack>

      <DataState query={q} empty={{ title: 'No appointments yet' }}>
        {() => filtered.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No appointments match this filter.
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {filtered.map((a) => <Row key={a.id} appointment={a} />)}
          </Stack>
        )}
      </DataState>
    </>
  );
}

function Row({ appointment }: { appointment: AppointmentRow }) {
  return (
    <Card>
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{
            width: 40, height: 40, borderRadius: 1.5,
            bgcolor: 'background.subtle', color: 'primary.main',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <EventIcon />
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Typography variant="subtitle1" fontWeight={600} noWrap>
                {appointment.procedure_name}
              </Typography>
              <Chip size="small" label={appointment.status.replace(/_/g, ' ')}
                    variant="outlined" sx={{ textTransform: 'capitalize' }} />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {appointment.clinician_name} · patient {appointment.patient_initials}
              {' · '}{appointment.modality.replace(/_/g, ' ')}
            </Typography>
          </Box>
          <Stack alignItems="flex-end" spacing={0.5}>
            <Typography variant="body2" className="tabular">
              {formatDateTime(appointment.start_at)}
            </Typography>
            {appointment.payment_status && (
              <Chip size="small"
                    label={appointment.payment_status}
                    color={appointment.payment_status === 'paid' ? 'success'
                           : appointment.payment_status === 'unpaid' ? 'warning'
                           : 'default'}
                    variant="outlined"
                    sx={{ height: 22 }} />
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}
